// src/app/pages/volunteer/volunteer.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EmailJsService } from '../../services/emailjs.service';
import { SigTrackerComponent } from '../../components/sig-tracker/sig-tracker.component';
import { MiMapComponent } from '../../components/mi-map/mi-map.component';
import { SeoService } from '../../services/seo.service';

interface Volunteer {
  name: string;
  email: string;
  zip: string;
  role: string;
}

interface PetitionRequest {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zip: string;
  county: string;
  quantity: string;
}

@Component({
  selector: 'app-volunteer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SigTrackerComponent,
    MiMapComponent
  ],
  templateUrl: './volunteer.component.html',
  styleUrls: ['./volunteer.component.css']
})
export class VolunteerComponent {

  // ── Volunteer signup ───────────────────────────────────────────────────────
  volunteer: Volunteer = {
    name: '',
    email: '',
    zip: '',
    role: ''
  };

  submitted = false;
  isSubmitting = false;
  errorMessage = '';

  // ── Petition mail request ──────────────────────────────────────────────────
  petition: PetitionRequest = {
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: '',
    county: '',
    quantity: '5'
  };

  petitionSubmitted = false;
  isPetitionSubmitting = false;
  petitionError = '';

  // ── Michigan counties ──────────────────────────────────────────────────────
  michiganCounties: string[] = [
    'Alcona','Alger','Allegan','Alpena','Antrim','Arenac','Baraga','Barry',
    'Bay','Benzie','Berrien','Branch','Calhoun','Cass','Charlevoix',
    'Cheboygan','Chippewa','Clare','Clinton','Crawford','Delta','Dickinson',
    'Eaton','Emmet','Genesee','Gladwin','Gogebic','Grand Traverse','Gratiot',
    'Hillsdale','Houghton','Huron','Ingham','Ionia','Iosco','Iron','Isabella',
    'Jackson','Kalamazoo','Kalkaska','Kent','Keweenaw','Lake','Lapeer',
    'Leelanau','Lenawee','Livingston','Luce','Mackinac','Macomb','Manistee',
    'Marquette','Mason','Mecosta','Menominee','Midland','Missaukee','Monroe',
    'Montcalm','Montmorency','Muskegon','Newaygo','Oakland','Oceana','Ogemaw',
    'Ontonagon','Osceola','Oscoda','Otsego','Ottawa','Presque Isle','Roscommon',
    'Saginaw','Saint Clair','Saint Joseph','Sanilac','Schoolcraft','Shiawassee',
    'Tuscola','Van Buren','Washtenaw','Wayne','Wexford'
  ];

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly volunteerPageId =
    `${this.baseUrl}/volunteer#volunteer`;

  private readonly volunteerActionId =
    `${this.baseUrl}/volunteer#join-campaign`;

  constructor(
    private emailJsService: EmailJsService,
    private seo: SeoService
  ) {}

  ngOnInit(): void {
    this.seo.update(
      'Volunteer & Constitutional Captains',
      'Help Lydia win Michigan. Become a Constitutional Captain and knock doors across Michigan. We need 15,000 by Election Day, November 3, 2026.',
      'volunteer'
    );

    this.seo.setStructuredData({
      "@context": "https://schema.org",

      "@graph": [
        {
          "@type": "WebPage",

          "@id": this.volunteerPageId,

          "url": `${this.baseUrl}/volunteer`,

          "name": "Volunteer & Constitutional Captains",

          "description":
            "Help Lydia win Michigan. Become a Constitutional Captain and knock doors across Michigan. We need 15,000 by Election Day, November 3, 2026.",

          "isPartOf": {
            "@id": this.seo.websiteId
          },

          "about": {
            "@id": this.seo.lydiaId
          },

          "publisher": {
            "@id": this.seo.campaignId
          },

          "mainEntity": {
            "@id": this.volunteerActionId
          },

          "inLanguage": "en-US"
        },

        {
          "@type": "JoinAction",

          "@id": this.volunteerActionId,

          "name": "Volunteer with Lydia Christensen's campaign",

          "description":
            "Join the campaign as a volunteer or Constitutional Captain.",

          "agent": {
            "@id": this.lydiaId
          },

          "object": {
            "@id": this.seo.campaignId
          },

          "target": {
            "@type": "EntryPoint",
            "urlTemplate":
              `${this.baseUrl}/volunteer`
          }
        }
      ]
    });
  }

  // ── Submit volunteer signup ────────────────────────────────────────────────
  async onSubmit(): Promise<void> {
    this.isSubmitting = true;
    this.errorMessage = '';
    this.submitted = false;

    try {
      await this.emailJsService.sendEmail(
        this.volunteer,
        'Volunteer'
      );

      this.submitted = true;

    } catch (error: any) {

      this.errorMessage =
        'Failed to send. Please try again or email senator@lydialynnchristensen.com.';

      console.error(
        'Volunteer email error:',
        error
      );

    } finally {

      this.isSubmitting = false;
    }
  }

  resetForm(): void {
    this.volunteer = {
      name: '',
      email: '',
      zip: '',
      role: ''
    };

    this.submitted = false;
    this.errorMessage = '';
  }

  // ── Submit petition mail request ───────────────────────────────────────────
  async onPetitionRequest(form: any): Promise<void> {

    if (form.invalid) {
      return;
    }

    this.isPetitionSubmitting = true;
    this.petitionError = '';

    try {

      await this.emailJsService.sendPetitionRequest(
        this.petition
      );

      this.petitionSubmitted = true;

    } catch (error: any) {

      this.petitionError =
        'Failed to send. Please try again or email senator@lydialynnchristensen.com.';

      console.error(
        'Petition request error:',
        error
      );

    } finally {

      this.isPetitionSubmitting = false;
    }
  }

  resetPetitionForm(): void {

    this.petition = {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zip: '',
      county: '',
      quantity: '5'
    };

    this.petitionSubmitted = false;
    this.petitionError = '';
  }

  private get lydiaId(): string {
    return this.seo.lydiaId;
  }
}